/* Prest custom JS on Dawn: review carousels, flavor tabs, bundle builder.
   Cart adds go through Dawn's cart drawer (cart-drawer custom element). */

(function () {
  'use strict';

  const money = (cents) => '$' + (cents / 100).toFixed(2);

  /* ---------------- Dawn cart integration ---------------- */

  const FRIENDLY_ADD_ERROR = 'Sorry — we couldn’t add that to your cart. Please try again.';

  function friendlyError(message) {
    const err = new Error(message);
    err.prestFriendly = true;
    return err;
  }

  async function addItemsToCart(items) {
    const cart =
      document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    const body = { items };

    try {
      if (cart && typeof cart.getSectionsToRender === 'function') {
        body.sections = cart.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
        if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
      }
    } catch (uiError) {
      /* Section prep failed (browser quirk) — add without drawer refresh data. */
      console.warn('[Prest] cart section prep failed:', uiError);
      delete body.sections;
      delete body.sections_url;
    }

    const res = await fetch(window.routes.cart_add_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      const msg = typeof err.description === 'string' && err.description ? err.description : null;
      throw friendlyError(msg || (window.cartStrings && window.cartStrings.error) || FRIENDLY_ADD_ERROR);
    }

    /* From here on the items ARE in the cart — nothing below may surface an
       error to the shopper (Safari has thrown internal DOM errors during the
       drawer refresh). */
    let state = null;
    try {
      state = await res.json();
    } catch (parseError) {
      console.warn('[Prest] cart add succeeded but response parse failed:', parseError);
    }

    try {
      if (cart && state && state.sections && typeof cart.renderContents === 'function') {
        cart.renderContents(state);
        if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
      } else {
        window.location.href = window.routes.cart_url;
      }
    } catch (uiError) {
      console.warn('[Prest] cart add succeeded but drawer refresh failed:', uiError);
      /* Show the shopper their cart instead of an error for a successful add. */
      window.location.href = window.routes.cart_url;
      return state;
    }

    try {
      reseedBuildersFromCart();
    } catch (reseedError) {
      console.warn('[Prest] builder reseed after add failed:', reseedError);
    }
    return state;
  }

  window.PrestCart = { addItems: addItemsToCart };

  /* ---------------- Shared cart state (builders mirror the cart) ---------------- */

  const prestBuilderReseeders = [];

  async function fetchPrestCart() {
    try {
      const res = await fetch('/cart.js');
      if (!res.ok) return null;
      return await res.json();
    } catch (e) {
      return null;
    }
  }

  async function reseedBuildersFromCart() {
    if (!prestBuilderReseeders.length) return null;
    const cart = await fetchPrestCart();
    if (cart) prestBuilderReseeders.forEach((fn) => fn(cart));
    return cart;
  }

  /* Change one cart line by key. The last call in a batch passes withSections
     so Dawn's drawer re-renders (and auto-opens) with the final state. */
  async function changeCartLine(key, quantity, withSections) {
    const cart =
      document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    const body = { id: key, quantity };

    if (withSections && cart && typeof cart.getSectionsToRender === 'function') {
      body.sections = cart.getSectionsToRender().map((section) => section.id);
      body.sections_url = window.location.pathname;
    }

    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || 'Could not update the cart');
    }

    const state = await res.json();

    if (withSections && cart && typeof cart.renderContents === 'function' && state.sections) {
      cart.renderContents(state);
      cart.classList.toggle('is-empty', state.item_count === 0);
    }
    return state;
  }

  /* ---------------- AJAX add for simple card forms ---------------- */

  function initAjaxForms() {
    document.querySelectorAll('form[data-ajax-cart]').forEach((form) => {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const btn = form.querySelector('[type="submit"]');
        const original = btn.textContent;
        btn.textContent = 'Adding…';
        btn.disabled = true;

        const fd = new FormData(form);
        const item = {
          id: parseInt(fd.get('id'), 10),
          quantity: parseInt(fd.get('quantity') || '1', 10),
        };
        const plan = fd.get('selling_plan');
        if (plan) item.selling_plan = parseInt(plan, 10);

        try {
          await addItemsToCart([item]);
        } catch (err) {
          alert(err.message);
        } finally {
          btn.textContent = original;
          btn.disabled = false;
        }
      });
    });
  }

  /* ---------------- Back-in-stock alerts (Klaviyo) ----------------
     Out-of-stock rows/pages render a [data-restock] block; subscribing posts
     to Klaviyo's public Back in Stock API, and the "Back in Stock" flow in
     Klaviyo emails the shopper when Shopify inventory returns. */

  var KLAVIYO_COMPANY_ID = 'RzSvMk';

  function initRestockNotify() {
    document.querySelectorAll('[data-restock]').forEach((box) => {
      const toggle = box.querySelector('[data-restock-toggle]');
      const form = box.querySelector('[data-restock-form]');
      const msg = box.querySelector('[data-restock-msg]');
      if (!toggle || !form || form.dataset.bound) return;
      form.dataset.bound = '1';

      toggle.addEventListener('click', () => {
        form.hidden = !form.hidden;
        if (!form.hidden) form.querySelector('input').focus();
      });

      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const email = form.querySelector('input').value.trim();
        if (!email) return;
        const btn = form.querySelector('[type="submit"]');
        btn.disabled = true;
        btn.textContent = 'Saving…';
        try {
          const res = await fetch(
            'https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=' + KLAVIYO_COMPANY_ID,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
              body: JSON.stringify({
                data: {
                  type: 'back-in-stock-subscription',
                  attributes: {
                    profile: { data: { type: 'profile', attributes: { email: email } } },
                    channels: ['EMAIL'],
                  },
                  relationships: {
                    variant: {
                      data: { type: 'catalog-variant', id: '$shopify:::$default:::' + box.dataset.variantId },
                    },
                  },
                },
              }),
            }
          );
          if (!res.ok) throw new Error('Klaviyo responded ' + res.status);
          form.hidden = true;
          toggle.hidden = true;
          msg.textContent = 'You’re on the list — we’ll email you as soon as it’s back!';
          msg.hidden = false;
        } catch (err) {
          btn.disabled = false;
          btn.textContent = 'Notify me';
          msg.textContent = 'Hmm, that didn’t go through — please try again.';
          msg.hidden = false;
        }
      });
    });
  }

  /* ---------------- Strip filler paragraphs from tab copy ----------------
     Shopify's rich-text editor pads descriptions with <p>&nbsp;</p> lines;
     CSS :empty can't match them, so remove them here. */

  function initCleanTabCopy() {
    document.querySelectorAll('.flavor-tabs__panel p').forEach((p) => {
      if (!p.textContent.replace(/ /g, ' ').trim() && !p.querySelector('img, iframe, video')) {
        p.remove();
      }
    });
  }

  /* ---------------- Product gallery ---------------- */

  function initGalleries() {
    document.querySelectorAll('[data-gallery]').forEach((gallery) => {
      const main = gallery.querySelector('.product-gallery__main img');
      if (!main) return;
      gallery.querySelectorAll('.product-gallery__thumb').forEach((thumb) => {
        thumb.addEventListener('click', () => {
          main.src = thumb.dataset.src;
          main.srcset = '';
          gallery.querySelectorAll('.product-gallery__thumb').forEach((t) => t.classList.remove('is-active'));
          thumb.classList.add('is-active');
        });
      });
    });
  }

  /* ---------------- Quantity steppers ---------------- */

  function initSteppers() {
    document.querySelectorAll('.qty-stepper[data-stepper]').forEach((stepper) => {
      const input = stepper.querySelector('input');
      stepper.querySelectorAll('button').forEach((btn) => {
        btn.addEventListener('click', () => {
          const delta = parseInt(btn.dataset.delta, 10);
          input.value = Math.max(1, parseInt(input.value || '1', 10) + delta);
          input.dispatchEvent(new Event('change'));
        });
      });
    });
  }

  /* ---------------- Transparent header over hero (Pistakio style) ---------------- */

  function initTransparentHeader() {
    const hero = document.querySelector('.hero-video');
    const headerWrapper = document.querySelector('.header-wrapper');
    if (!hero || !headerWrapper) return;
    const headerSection = headerWrapper.closest('.shopify-section');
    const heroSection = hero.closest('.shopify-section') || hero;

    headerSection.classList.add('prest-glass');

    const pullUp = () => {
      const h = headerWrapper.offsetHeight;
      heroSection.style.marginTop = `-${h}px`;
      hero.style.paddingTop = `${h}px`;
    };
    pullUp();
    window.addEventListener('resize', pullUp);

    /* Cream wordmark over the video, green wordmark on the solid header */
    const logo = headerWrapper.querySelector('.header__heading-logo');
    const CREAM_LOGO = 'https://cdn.shopify.com/s/files/1/0708/6777/9677/files/Prest_Logo_Cream.png';
    let greenLogo = null;
    if (logo) {
      greenLogo = { src: logo.src, srcset: logo.srcset };
      new Image().src = CREAM_LOGO; // preload so the swap never flashes
    }

    const sync = () => {
      const solid = window.scrollY > 24;
      headerSection.classList.toggle('prest-solid', solid);
      if (logo) {
        if (solid && logo.src !== greenLogo.src) {
          logo.src = greenLogo.src;
          logo.srcset = greenLogo.srcset;
        } else if (!solid && !logo.src.includes('Prest_Logo_Cream')) {
          logo.srcset = '';
          logo.src = CREAM_LOGO;
        }
      }
    };
    window.addEventListener('scroll', sync, { passive: true });
    sync();
  }

  /* ---------------- Success popups (newsletter / contact) ---------------- */

  function initPopups() {
    document.addEventListener('click', (e) => {
      const closer = e.target.closest('[data-popup-close]');
      if (closer) closer.closest('[data-prest-popup]')?.remove();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') document.querySelector('[data-prest-popup]')?.remove();
    });
  }

  /* ---------------- Review carousel arrows ---------------- */

  function initCarousels() {
    document.querySelectorAll('[data-carousel]').forEach((section) => {
      const track = section.querySelector('.reviews-carousel__track');
      if (!track) return;
      section.querySelectorAll('.carousel-arrow').forEach((arrow) => {
        arrow.addEventListener('click', () => {
          const dir = parseInt(arrow.dataset.dir, 10);
          track.scrollBy({ left: dir * (track.firstElementChild.offsetWidth + 16), behavior: 'smooth' });
        });
      });
    });
  }

  /* ---------------- Flavor detail tabs ---------------- */

  function initFlavorTabs() {
    document.querySelectorAll('[data-flavor-tabs]').forEach((root) => {
      const tabs = [...root.querySelectorAll('.flavor-tabs__tab')];
      const panels = [...root.querySelectorAll('.flavor-tabs__panel')];
      tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => {
          tabs.forEach((t) => t.classList.remove('is-active'));
          panels.forEach((p) => (p.hidden = true));
          tab.classList.add('is-active');
          panels[i].hidden = false;
        });
      });
    });
  }

  /* ---------------- Bundle builder (shared by product + bundle pages) ---------------- */

  function initBuilders() {
    document.querySelectorAll('[data-builder]').forEach((root) => {
      const flavors = [...root.querySelectorAll('[data-flavor]')];
      const stops = [...root.querySelectorAll('.tier-slider__stop')];
      const fill = root.querySelector('.tier-slider__fill');
      const puck = root.querySelector('.tier-slider__puck');
      const addBtn = root.querySelector('[data-bundle-add]');
      const totalEl = root.querySelector('[data-total]');
      const compareEl = root.querySelector('[data-compare]');
      const progressLabel = root.querySelector('[data-progress-label]');
      const progressFill = root.querySelector('[data-progress-fill]');
      const modeWrap = root.querySelector('[data-bundle-mode]');
      const planSelect = root.querySelector('[data-bundle-plan]');
      const shipFlag = root.querySelector('.tier-slider__ship');
      const minFlag = root.querySelector('.tier-slider__min');
      const emptyCta = root.dataset.emptyCta || 'Add 3 pouches to start';
      const addCta = root.dataset.addCta || 'Add to cart';
      const minQty = parseInt(root.dataset.minQty || '3', 10);
      const freeShipQty = parseInt(root.dataset.freeShipQty || '0', 10);
      const subscribePercent = parseFloat(root.dataset.subscribePercent || '0');
      const sliderMax = Math.max(...stops.map((s) => parseInt(s.dataset.qty, 10)), 1);

      /* Rail position (%) for a quantity. The stretch below the minimum is
         compressed to one pouch step; must match snippets/prest-slider-pos. */
      const sliderPos = (q) => {
        const stepsAfter = sliderMax - minQty;
        let pos;
        if (minQty > 0 && stepsAfter > 0) {
          const lead = 100 / (stepsAfter + 1);
          pos = q >= minQty ? lead + ((q - minQty) * (100 - lead)) / stepsAfter : (q * lead) / minQty;
        } else {
          pos = (q / sliderMax) * 100;
        }
        return Math.min(100, pos);
      };

      const counts = new Map(flavors.map((f) => [f, parseInt(f.dataset.preselect || '0', 10)]));

      /* Cart mirroring: once the cart holds pouches, the builder shows the
         cart's quantities and the CTA becomes "Update cart". */
      const cartNote = root.querySelector('[data-cart-note]');
      const seedCounts = new Map();
      let flavorLines = new Map(); /* variantId -> [{key, quantity, selling_plan}] */
      let cartSeeded = false;
      let isDirty = false;

      function seedFromCart(cart) {
        flavorLines = new Map();
        const planIds = new Set();
        let allLines = [];
        let pouchTotal = 0;
        flavors.forEach((f) => {
          const vid = parseInt(f.dataset.variantId, 10);
          const lines = (cart.items || [])
            .filter((i) => i.variant_id === vid)
            .map((i) => ({
              key: i.key,
              quantity: i.quantity,
              selling_plan: i.selling_plan_allocation ? i.selling_plan_allocation.selling_plan.id : null,
            }));
          flavorLines.set(vid, lines);
          allLines = allLines.concat(lines);
          lines.forEach((l) => { if (l.selling_plan) planIds.add(l.selling_plan); });
          pouchTotal += lines.reduce((s, l) => s + l.quantity, 0);
        });

        cartSeeded = pouchTotal > 0;
        seedCounts.clear();
        flavors.forEach((f) => {
          const vid = parseInt(f.dataset.variantId, 10);
          const q = (flavorLines.get(vid) || []).reduce((s, l) => s + l.quantity, 0);
          seedCounts.set(f, cartSeeded ? q : 0);
          counts.set(f, cartSeeded ? q : parseInt(f.dataset.preselect || '0', 10));
        });

        /* Mirror the cart's purchase mode when every pouch line shares one plan */
        if (modeWrap && !modeWrap.hidden && cartSeeded) {
          const subscribe = allLines.length > 0 && allLines.every((l) => l.selling_plan) && planIds.size === 1;
          const radio = modeWrap.querySelector(subscribe ? 'input[value="subscribe"]' : 'input[value="onetime"]');
          if (radio && !radio.checked) {
            radio.checked = true;
            modeWrap.querySelectorAll('.purchase-option').forEach((o) => {
              o.classList.toggle('is-selected', o.querySelector('input').checked);
            });
          }
          if (subscribe && planSelect) planSelect.value = String([...planIds][0]);
        }

        if (cartNote) cartNote.hidden = !cartSeeded;
        update();
      }

      /* populate frequency dropdown from the first flavor that has selling plans */
      const planned = flavors.find((f) => f.dataset.sellingPlans);
      let plans = [];
      if (planned && planSelect) {
        plans = JSON.parse(planned.dataset.sellingPlans);
        planSelect.innerHTML = plans.map((p) => `<option value="${p.id}">${p.name}</option>`).join('');
        planSelect.hidden = plans.length < 2;
      }
      if (modeWrap && !plans.length) modeWrap.hidden = true;

      const tierFor = (qty) => {
        let best = null;
        stops.forEach((t) => {
          const need = parseInt(t.dataset.qty, 10);
          if (qty >= need && (!best || need > parseInt(best.dataset.qty, 10))) best = t;
        });
        return best;
      };

      const nextTierAfter = (qty) => {
        let next = null;
        stops.forEach((t) => {
          const need = parseInt(t.dataset.qty, 10);
          if (need > qty && (!next || need < parseInt(next.dataset.qty, 10))) next = t;
        });
        return next;
      };

      const isSubscribe = () =>
        modeWrap && !modeWrap.hidden && modeWrap.querySelector('input[value="subscribe"]')?.checked;

      function update() {
        let qty = 0;
        let subtotal = 0;
        counts.forEach((count, f) => {
          qty += count;
          subtotal += count * parseInt(f.dataset.price, 10);
          const stepper = f.querySelector('[data-flavor-stepper]');
          const addOne = f.querySelector('[data-add]');
          if (stepper) stepper.hidden = count === 0;
          if (addOne) addOne.hidden = count > 0;
          const countEl = f.querySelector('[data-count]');
          if (countEl) countEl.textContent = count;
          f.classList.toggle('has-items', count > 0);
        });

        const active = tierFor(qty);
        stops.forEach((t) => t.classList.toggle('is-reached', qty >= parseInt(t.dataset.qty, 10)));

        /* Free-shipping flag wiggles the moment the threshold is reached */
        if (shipFlag && freeShipQty > 0) {
          const reached = qty >= freeShipQty;
          if (reached && !shipFlag.classList.contains('is-reached')) {
            shipFlag.classList.remove('is-reached');
            void shipFlag.offsetWidth; /* restart the animation */
            shipFlag.classList.add('is-reached');
          } else if (!reached) {
            shipFlag.classList.remove('is-reached');
          }
        }

        /* Minimum-order flag wiggles the moment the minimum is reached */
        if (minFlag) {
          const minReached = qty >= minQty;
          if (minReached && !minFlag.classList.contains('is-reached')) {
            minFlag.classList.remove('is-reached');
            void minFlag.offsetWidth; /* restart the animation */
            minFlag.classList.add('is-reached');
          } else if (!minReached) {
            minFlag.classList.remove('is-reached');
          }
        }

        const percent = active ? parseFloat(active.dataset.percent) : 0;
        let discounted = subtotal * (1 - percent / 100);
        if (isSubscribe() && subscribePercent > 0) {
          discounted = discounted * (1 - subscribePercent / 100);
        }
        discounted = Math.round(discounted);

        /* Per-pouch price on each flavor row tracks the active tier (+ subscription),
           with a per-8oz-bowl breakdown — one pouch makes 3 bowls. */
        const unitMult =
          (1 - percent / 100) * (isSubscribe() && subscribePercent > 0 ? 1 - subscribePercent / 100 : 1);
        flavors.forEach((f) => {
          const base = parseInt(f.dataset.price, 10);
          const unit = Math.round(base * unitMult);
          const rowPrice = f.querySelector('[data-row-price]');
          const rowCompare = f.querySelector('[data-row-compare]');
          const rowBowl = f.querySelector('[data-row-bowl]');
          if (rowPrice) rowPrice.textContent = money(unit);
          if (rowCompare) {
            rowCompare.hidden = unitMult >= 0.999;
            rowCompare.textContent = money(base);
          }
          if (rowBowl) rowBowl.textContent = money(Math.round(unit / 3)) + ' per 8oz bowl';
        });

        if (fill) fill.style.width = sliderPos(qty) + '%';
        if (puck) puck.style.left = sliderPos(qty) + '%';

        totalEl.textContent = money(discounted);
        if (discounted < subtotal && subtotal > 0) {
          compareEl.hidden = false;
          compareEl.textContent = money(subtotal);
        } else {
          compareEl.hidden = true;
        }

        const next = nextTierAfter(qty);
        if (qty === 0) {
          progressLabel.textContent = emptyCta;
          progressFill.style.width = '0%';
        } else if (qty < minQty) {
          const needed = minQty - qty;
          progressLabel.textContent = 'Add ' + needed + ' more pouch' + (needed === 1 ? '' : 'es') + ' · minimum of ' + minQty;
          progressFill.style.width = Math.min(100, (qty / minQty) * 100) + '%';
        } else if (freeShipQty > 0 && qty < freeShipQty && (!next || freeShipQty <= parseInt(next.dataset.qty, 10))) {
          const parts = [];
          if (next) parts.push('Add ' + (parseInt(next.dataset.qty, 10) - qty) + ' more for ' + next.dataset.percent + '% off');
          parts.push('free shipping at ' + freeShipQty + '+');
          progressLabel.textContent = parts.join(' · ');
          progressFill.style.width = Math.min(100, (qty / (next ? parseInt(next.dataset.qty, 10) : freeShipQty)) * 100) + '%';
        } else if (next) {
          progressLabel.textContent = 'Add ' + (parseInt(next.dataset.qty, 10) - qty) + ' more to unlock ' + next.dataset.percent + '% off';
          progressFill.style.width = Math.min(100, (qty / parseInt(next.dataset.qty, 10)) * 100) + '%';
        } else {
          let label = percent > 0 ? 'Max savings unlocked — ' + percent + '% off!' : 'Ready to go!';
          if (freeShipQty > 0 && qty >= freeShipQty) label += ' + FREE shipping';
          progressLabel.textContent = label;
          progressFill.style.width = '100%';
        }

        isDirty = false;
        counts.forEach((c, f) => {
          if (c !== (seedCounts.get(f) || 0)) isDirty = true;
        });

        const pouches = qty + ' pouch' + (qty === 1 ? '' : 'es');
        if (qty === 0) {
          addBtn.disabled = !cartSeeded;
          addBtn.textContent = cartSeeded ? 'Remove pouches from cart' : emptyCta;
        } else if (qty < minQty) {
          addBtn.disabled = true;
          addBtn.textContent = 'Minimum order · ' + minQty + ' pouches';
        } else if (cartSeeded) {
          addBtn.disabled = false;
          addBtn.textContent = isDirty ? 'Update cart · ' + pouches : 'In your cart · ' + pouches;
        } else {
          addBtn.disabled = false;
          addBtn.textContent = addCta + ' · ' + pouches;
        }
      }

      flavors.forEach((f) => {
        const add = f.querySelector('[data-add]');
        const plus = f.querySelector('[data-plus]');
        const minus = f.querySelector('[data-minus]');
        if (add) add.addEventListener('click', () => { counts.set(f, 1); update(); });
        if (plus) plus.addEventListener('click', () => { counts.set(f, counts.get(f) + 1); update(); });
        if (minus) minus.addEventListener('click', () => { counts.set(f, Math.max(0, counts.get(f) - 1)); update(); });
      });

      stops.forEach((t) => {
        t.addEventListener('click', () => {
          const target = parseInt(t.dataset.qty, 10);
          let current = 0;
          counts.forEach((c) => (current += c));
          if (current >= target) return;
          let deficit = target - current;
          const available = flavors.filter((f) => f.dataset.available !== 'false');
          let i = 0;
          while (deficit > 0 && available.length) {
            const f = available[i % available.length];
            counts.set(f, counts.get(f) + 1);
            deficit--;
            i++;
          }
          update();
        });
      });

      if (modeWrap) {
        modeWrap.querySelectorAll('input[name^="bundle_mode"]').forEach((r) => {
          r.addEventListener('change', () => {
            modeWrap.querySelectorAll('.purchase-option').forEach((o) => {
              o.classList.toggle('is-selected', o.querySelector('input').checked);
            });
            update();
          });
        });
      }

      /* Sync the builder's selections into the cart: adjust existing lines by
         key, add brand-new flavors, keep each existing line's selling plan. */
      async function applyToCart() {
        const planId = isSubscribe() && planSelect && planSelect.value ? parseInt(planSelect.value, 10) : null;
        const changes = [];
        const adds = [];
        flavors.forEach((f) => {
          const vid = parseInt(f.dataset.variantId, 10);
          const desired = counts.get(f) || 0;
          const lines = flavorLines.get(vid) || [];
          if (lines.length) {
            if (lines[0].quantity !== desired) changes.push({ key: lines[0].key, quantity: desired });
            lines.slice(1).forEach((l) => changes.push({ key: l.key, quantity: 0 }));
          } else if (desired > 0) {
            const item = { id: vid, quantity: desired };
            if (planId) item.selling_plan = planId;
            adds.push(item);
          }
        });

        for (let i = 0; i < changes.length; i++) {
          const isLast = i === changes.length - 1 && adds.length === 0;
          await changeCartLine(changes[i].key, changes[i].quantity, isLast);
        }
        if (adds.length) await addItemsToCart(adds);
      }

      addBtn.addEventListener('click', async () => {
        let qtyNow = 0;
        counts.forEach((c) => (qtyNow += c));

        /* Nothing changed — just show the cart */
        if (cartSeeded && !isDirty && qtyNow >= minQty) {
          const drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.open === 'function') drawer.open();
          else window.location.href = '/cart';
          return;
        }

        if (qtyNow > 0 && qtyNow < minQty) return;
        if (qtyNow === 0 && !cartSeeded) return;

        addBtn.disabled = true;
        const original = addBtn.textContent;
        addBtn.textContent = cartSeeded ? 'Updating…' : 'Adding…';
        try {
          if (cartSeeded) {
            await applyToCart();
          } else {
            const planId = isSubscribe() && planSelect && planSelect.value ? parseInt(planSelect.value, 10) : null;
            const items = [];
            counts.forEach((count, f) => {
              if (count > 0) {
                const item = { id: parseInt(f.dataset.variantId, 10), quantity: count };
                if (planId) item.selling_plan = planId;
                items.push(item);
              }
            });
            await addItemsToCart(items);
          }
          await reseedBuildersFromCart();
        } catch (err) {
          alert(err.message);
          addBtn.textContent = original;
          addBtn.disabled = false;
          update();
        }
      });

      prestBuilderReseeders.push(seedFromCart);
      update();
    });
  }

  /* ---------------- Cart minimum (3 pouches) guard ---------------- */

  const CART_MIN_QTY = 3;
  const POUCH_HANDLES = [
    'garlic-shelf-stable-hummus',
    'spicy-harissa-shelf-stable-hummus-1',
    'lemon-dill-shelf-stable-hummus',
  ];

  function initCartMinimum() {
    /* Pouch variant ids are static per product; resolve them once at load */
    const pouchVariantIds = new Set();
    POUCH_HANDLES.forEach((handle) => {
      fetch('/products/' + handle + '.js')
        .then((r) => (r.ok ? r.json() : null))
        .then((p) => p && p.variants.forEach((v) => pouchVariantIds.add(String(v.id))))
        .catch(() => {});
    });

    const isPouchInput = (input) =>
      input &&
      input.classList &&
      input.classList.contains('quantity__input') &&
      pouchVariantIds.has(String(input.dataset.quantityVariantId));

    /* Scope totals to the cart context being interacted with (page vs drawer) */
    const contextFor = (el) =>
      (el && el.closest && (el.closest('cart-drawer') || el.closest('cart-items'))) || document;

    const pouchTotal = (ctx) =>
      [...ctx.querySelectorAll('input.quantity__input')]
        .filter(isPouchInput)
        .reduce((sum, input) => sum + (parseInt(input.value, 10) || 0), 0);

    let toastTimer;
    function showMinMessage(nearEl) {
      let msg = 'Minimum order is ' + CART_MIN_QTY + ' pouches. Add another flavor if you’d like to replace.';
      const line = nearEl && nearEl.closest('.cart-item');
      const inline = line && line.querySelector('.cart-item__error-text');
      if (inline) {
        inline.textContent = msg;
        clearTimeout(toastTimer);
        toastTimer = setTimeout(() => { inline.textContent = ''; }, 5000);
        return;
      }
      let toast = document.querySelector('.prest-cart-min-toast');
      if (!toast) {
        toast = document.createElement('div');
        toast.className = 'prest-cart-min-toast';
        document.body.appendChild(toast);
      }
      toast.textContent = msg;
      toast.classList.add('is-visible');
      clearTimeout(toastTimer);
      toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 5000);
    }

    /* Block minus / remove clicks that would leave exactly 1 pouch in the cart */
    document.addEventListener(
      'click',
      (evt) => {
        const removeBtn = evt.target.closest && evt.target.closest('cart-remove-button');
        const minusBtn = evt.target.closest && evt.target.closest('quantity-input .quantity__button[name="minus"]');
        const checkoutBtn = evt.target.closest && evt.target.closest('button[name="checkout"]');

        if (checkoutBtn) {
          const total = pouchTotal(contextFor(checkoutBtn));
          if (total > 0 && total < CART_MIN_QTY) {
            evt.preventDefault();
            evt.stopImmediatePropagation();
            showMinMessage(null);
          }
          return;
        }

        let input = null;
        let newLineQty = null;

        if (removeBtn) {
          const line = removeBtn.closest('.cart-item') || removeBtn.closest('tr');
          const lineInput = line && line.querySelector('input.quantity__input');
          if (isPouchInput(lineInput)) {
            input = lineInput;
            newLineQty = 0;
          }
        } else if (minusBtn) {
          const stepInput = minusBtn.closest('quantity-input').querySelector('input.quantity__input');
          if (isPouchInput(stepInput)) {
            input = stepInput;
            newLineQty = Math.max(0, (parseInt(stepInput.value, 10) || 0) - 1);
          }
        }

        if (!input || newLineQty === null) return;
        const current = parseInt(input.value, 10) || 0;
        const prospective = pouchTotal(contextFor(input)) - current + newLineQty;
        if (prospective > 0 && prospective < CART_MIN_QTY) {
          evt.preventDefault();
          evt.stopImmediatePropagation();
          showMinMessage(input);
        }
      },
      true
    );

    /* Block typed quantity changes that would leave exactly 1 pouch */
    document.addEventListener(
      'change',
      (evt) => {
        const input = evt.target;
        if (!isPouchInput(input)) return;
        const oldVal = parseInt(input.defaultValue, 10) || 0;
        /* input.value already holds the new quantity, so pouchTotal() is the prospective total */
        const prospective = pouchTotal(contextFor(input));
        if (prospective > 0 && prospective < CART_MIN_QTY) {
          evt.preventDefault();
          evt.stopImmediatePropagation();
          input.value = oldVal;
          showMinMessage(input);
        }
      },
      true
    );
  }

  /* ---------------- Clear cart (cart page) ---------------- */

  function initClearCart() {
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest && e.target.closest('[data-prest-clear-cart]');
      if (!btn) return;
      e.preventDefault();
      if (!window.confirm('Remove everything from your cart?')) return;
      btn.disabled = true;
      const original = btn.textContent;
      btn.textContent = 'Clearing…';
      try {
        const res = await fetch('/cart/clear.js', { method: 'POST' });
        if (!res.ok) throw new Error('Could not clear the cart');
        window.location.reload();
      } catch (err) {
        btn.disabled = false;
        btn.textContent = original;
        alert('Could not clear the cart. Please try again.');
      }
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    initTransparentHeader();
    initPopups();
    initAjaxForms();
    initCleanTabCopy();
    initGalleries();
    initSteppers();
    initCarousels();
    initFlavorTabs();
    initBuilders();
    initCartMinimum();
    initClearCart();
    initRestockNotify();

    /* Builders mirror the cart: seed on load and after any cart change made
       elsewhere (drawer steppers, remove buttons, etc.). 'cart-update' is
       Dawn's PUB_SUB_EVENTS.cartUpdate — the const isn't exposed on window,
       so the literal event name is used here. */
    reseedBuildersFromCart();
    if (typeof window.subscribe === 'function') {
      window.subscribe('cart-update', () => {
        reseedBuildersFromCart();
      });
    }
  });
})();
